import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import Moment from "moment";
import MaterialModal from "../MaterialModal";
import { hideModal } from "../../../actions/actionCreators";
import { checkFlowPermission, goEditFlow } from "../../../actions/flowActions";
import { getNotifications } from "../../../actions/userNotificationActions";
import { parseDateToTimeZone } from "../../../helpers/parseDate";
import { debounce } from "lodash";
import withModal from "../withModal";
import { FormControl, Select, MenuItem, Tooltip, TextField } from "@material-ui/core";

// generic material ui table with sorting and paging
import { Table, Paper } from "@material-ui/core";
import TableHeader from "../../material-components/Table/TableHeader";
import TableBody from "../../material-components/Table/TableBody";
import TableFooter from "../../material-components/Table/TableFooter";
import LoadingTable from "../../material-components/WidgetSkeleton/LoadingTable";
import { stableSort, getSorting } from "../../../helpers/materialTableFunctions";

const dateOptions = [
    { label: "Today", value: "today" },
    { label: "Last 1 Day", value: "oneDay" },
    { label: "Last 7 Days", value: "sevenDay" },
    { label: "Last 30 Days", value: "thirtyDay" },
];

const dateSelectedValues = {
    today: Moment().startOf("day").utc().format(),
    oneDay: Moment().subtract(1, "days").startOf("day").utc().format(),
    sevenDay: Moment().subtract(1, "weeks").startOf("day").utc().format(),
    thirtyDay: Moment().subtract(1, "months").startOf("day").utc().format(),
};

const searchByOptions = [
    { label: "Source Name", value: "SourceName" },
    { label: "Company", value: "CompanyName" },
];

const NotificationsModal = ({ isOpen }) => {
    const dispatch = useDispatch();

    const [selectedDate, setSelectedDate] = useState(dateOptions[0].value);
    const [selectedSearchOption, setSelectedSearchOption] = useState(searchByOptions[0].value);
    const [searchByValue, setSearchByValue] = useState("");
    const [showErrors, setShowErrors] = useState(true);
    const [showEvents, setShowEvents] = useState(true);
    const [page, setPage] = useState(0);
    const [filteredNotifications, setFilteredNotifications] = useState([]);
    const [order, setOrder] = useState("CreatedDate");
    const [orderBy, setOrderBy] = useState("desc");
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const loading = useSelector(state => state.userNotifications.loading);
    const userNotifications = useSelector(state => state.userNotifications.userNotifications || []);
    const timeZoneIana = useSelector(state => state.session.userSettings["time-zone-iana"]);

    const fetchNotifications = debounce(() => {
        dispatch(getNotifications(dateSelectedValues[selectedDate]));
    }, 1000);

    useEffect(() => {
        if (isOpen) {
            dispatch(getNotifications());
        }
    }, [dispatch, isOpen]);

    useEffect(() => {
        let newFilteredNotifications = [...userNotifications];
        newFilteredNotifications = newFilteredNotifications.filter(
            x => (showErrors && x.Type == "Error") || (showEvents && x.Type == "Event")
        );

        if (searchByValue) {
            newFilteredNotifications = newFilteredNotifications.filter(x =>
                x[selectedSearchOption].toUpperCase().includes(searchByValue.toUpperCase())
            );
        }

        setFilteredNotifications(newFilteredNotifications);
    }, [selectedSearchOption, searchByValue, showErrors, showEvents, userNotifications]);

    const hide = () => {
        setSelectedDate(dateOptions[0].value);
        setSelectedSearchOption(searchByOptions[0].value);
        setSearchByValue("");
        setShowErrors(true);
        setShowEvents(true);
        setPage(0);
        dispatch(hideModal("USER_NOTIFICATIONS"));
    };

    const handleSearchByChange = e => {
        setSelectedSearchOption(e.target.value);
    };

    const debouncedSearch = debounce(search => {
        setSearchByValue(search);
    }, 1000);

    const handleSearch = e => {
        debouncedSearch(e.target.value);
        e.preventDefault();
    };

    const handleTimezoneChange = e => {
        setSelectedDate(e.target.value);
        dispatch(getNotifications(dateSelectedValues[e.target.value]));
    };

    const handleShowErrors = val => {
        setShowErrors(val.target.checked);
        setPage(0);
    };

    const handleShowEvents = val => {
        setShowEvents(val.target.checked);
        setPage(0);
    };

    const goToFlow = flowId => {
        dispatch(goEditFlow(flowId));
        if (window.location.href.includes("/ae/")) {
            checkFlowPermission(flowId);
        }
        hide();
    };

    const timeStamp = (createdDate, type) => {
        let timestamp;
        const formattedWaterfallDate = parseDateToTimeZone(createdDate, timeZoneIana);

        const nowDate = parseDateToTimeZone(new Date(), timeZoneIana);
        if (Moment(new Date(formattedWaterfallDate)).format() > Moment().subtract(1, "days").format()) {
            switch (type) {
                case "Event":
                case "Error":
                    timestamp = `${(timestamp = Moment(new Date(formattedWaterfallDate)).from(nowDate))}`;
                    break;
            }
        } else {
            timestamp = Moment(new Date(formattedWaterfallDate)).format("LLLL");
        }

        return timestamp;
    };

    const footer = (
        <div>
            <button type="button" className="btn btn-default" onClick={hide}>
                Close
            </button>
        </div>
    );

    const handleRequestSort = (e, property) => {
        if (order == "asc") {
            setOrder("desc");
        } else {
            setOrder("asc");
        }
        setOrderBy(property);
    };

    const renderType = type => (
        <div
            className={
                type == "Error"
                    ? "notification-label notification-message label white error"
                    : "notification-label notification-message label white"
            }
            style={{ fontSize: "12px" }}
        >
            {type}
        </div>
    );

    const renderSourceName = row => {
        if (row.SourceType == "Flow") {
            return (
                <div>
                    <a href="#" onClick={() => goToFlow(row.SourceId)}>
                        {row.SourceName}
                    </a>
                </div>
            );
        } else {
            return renderTextCell(row.SourceName);
        }
    };

    const renderMessage = (subject, message) => (
        <div style={{ fontSize: "12px" }}>
            <p className="notification-subject">{subject}</p>
            <span className="notification-message">{message}</span>
        </div>
    );

    const renderTextCell = value => <div style={{ fontSize: "12px" }}>{value}</div>;

    const renderTable = () => {
        const emptyMessage = ["EmptyMessage", "No data to display."];

        const headers = [
            ["Type", "Type"],
            ["SourceType", "Source"],
            ["SourceName", "Source Name"],
            ["Message", "Subject / Message"],
            ["CompanyName", "Company"],
            ["CreatedDate", "Date"],
        ];

        const tableData = stableSort(filteredNotifications, getSorting(order, orderBy)).map(x => [
            ["Type", renderType(x.Type)],
            ["SourceType", renderTextCell(x.SourceType)],
            ["SourceName", renderSourceName(x)],
            ["Message", renderMessage(x.Subject, x.Message)],
            ["CompanyName", renderTextCell(x.CompanyName)],
            ["CreatedDate", renderTextCell(timeStamp(x.CreatedDate, x.Type))],
        ]);

        return (
            <Paper>
                <Table style={{}}>
                    <TableHeader
                        headers={headers}
                        orderBy={orderBy}
                        order={order}
                        onClick={handleRequestSort}
                        widget={false}
                    />
                    {loading ? (
                        <LoadingTable cols={6} rows={rowsPerPage} />
                    ) : (
                        <TableBody
                            data={tableData}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            emptyMessage={emptyMessage}
                            widget={false}
                        />
                    )}
                </Table>
                <TableFooter
                    displayDense={false}
                    displayPaging={true}
                    rowsPerPageOptions={[rowsPerPage]}
                    count={filteredNotifications.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => {
                        setPage(newPage);
                    }}
                    onRowsPerPageChange={e => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>
        );
    };

    return (
        <MaterialModal
            title="Your Notifications"
            isOpen={isOpen}
            onClose={hide}
            id="notification-modals"
            fullWidth
            maxWidth="xl"
            showPrimaryButton={false}
            footer={footer}
        >
            <div>
                <div className="notification-filters">
                    <FormControl style={{ width: "100%", margin: "10px 0px" }}>
                        <Select onChange={handleSearchByChange} value={selectedSearchOption} disabled={loading}>
                            {searchByOptions.map((option, i) => (
                                <MenuItem key={i} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        onChange={handleSearch}
                        placeholder="Search..."
                        disabled={loading}
                        style={{
                            margin: "10px",
                            padding: "3px",
                            fontSize: "17px",
                        }}
                    />
                </div>
                <div className="notification-filters" style={{ justifyContent: "center" }}>
                    <label
                        className="md-checkbox"
                        style={{ marginLeft: "15px", marginRight: "45px", marginTop: "15px" }}
                    >
                        <input type="checkbox" onChange={handleShowEvents} checked={showEvents} disabled={loading} />
                        <span>Events</span>
                    </label>
                    <label className="md-checkbox" style={{ marginRight: "45px", marginTop: "15px" }}>
                        <input type="checkbox" onChange={handleShowErrors} checked={showErrors} disabled={loading} />
                        <span>Errors</span>
                    </label>
                    <FormControl style={{ width: "100%", margin: "10px 0px" }}>
                        <Select onChange={handleTimezoneChange} value={selectedDate} disabled={loading}>
                            {dateOptions.map((option, i) => (
                                <MenuItem key={i} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Tooltip title="Refresh">
                        <i
                            disabled={loading}
                            className="material-icons view"
                            style={{ margin: "12px 0px" }}
                            onClick={fetchNotifications}
                        >
                            refresh
                        </i>
                    </Tooltip>
                </div>
                <br />
                {renderTable()}
                {!loading && (
                    <label style={{ marginTop: "10px", float: "right" }}>
                        <span>Total Notifications: {userNotifications.length}</span>
                    </label>
                )}
            </div>
        </MaterialModal>
    );
};

NotificationsModal.propTypes = {
    isOpen: PropTypes.bool,
    hideModal: PropTypes.func,
    timeZoneIana: PropTypes.string,
    goEditFlow: PropTypes.func,
    checkFlowPermission: PropTypes.func,
};

export default withModal(NotificationsModal, "USER_NOTIFICATIONS");
